import { LazyChatMarkdown } from '../ChatMarkdown/LazyChatMarkdown'
import { TIER_PILL_CLASSES, TIER_TEXT_CLASSES, severityTier } from '../primitives/severityTier'
import { cn } from '../../lib/utils'
import {
  enumTone,
  formatScalar,
  getDisplayHint,
  humanizeKey,
  isAllScalarObject,
  isObjectArray,
  isScalar,
  isUnitIntervalSchema,
  itemObjectProperties,
  objectPropertiesForValue,
  schemaEnumValues,
  shouldOmitUnitKey,
  attachedUnit,
  type SchemaProperty
} from './schema-aware-result'

interface SchemaAwareResultViewProps {
  /** Schema-validated result document (or any nested object/value). */
  value: unknown
  /** JSON Schema describing `value` (root object schema, or a property schema). */
  schema?: unknown
  className?: string
  /** Nesting depth — used to tone down nested chrome. */
  depth?: number
}

/**
 * Schema-aware renderer for custom-agent result documents. Walks a JSON Schema
 * (when present) to choose KPI / table / markdown / nested layouts; falls back
 * to pretty JSON when the shape is unknown or the value does not match.
 */
export function SchemaAwareResultView({
  value,
  schema,
  className,
  depth = 0
}: SchemaAwareResultViewProps) {
  if (value === null || value === undefined) {
    return <span className={cn(bodyTextClass(depth), 'text-ink-3', className)}>—</span>
  }

  const propSchema =
    schema !== null && typeof schema === 'object' && !Array.isArray(schema)
      ? (schema as Record<string, unknown>)
      : null
  const display = propSchema ? getDisplayHint(propSchema) : undefined

  if (display === 'json') {
    return <JsonFallback value={value} className={className} />
  }

  if (typeof value === 'string' && (display === 'markdown' || looksLikeMarkdown(value))) {
    return (
      <div className={cn('prose prose-sm dark:prose-invert max-w-none text-ink-2', className)}>
        <LazyChatMarkdown>{value}</LazyChatMarkdown>
      </div>
    )
  }

  if (isScalar(value) && !Array.isArray(value)) {
    if (display === 'kpi' || (depth === 0 && typeof value === 'number')) {
      return (
        <KpiCard
          label={undefined}
          value={value}
          schema={propSchema ?? undefined}
          className={className}
        />
      )
    }
    return (
      <span className={cn(bodyTextClass(depth), className)}>
        <ScalarValue value={value} schema={propSchema ?? undefined} />
      </span>
    )
  }

  if (Array.isArray(value)) {
    return (
      <ArrayView
        value={value}
        schema={propSchema}
        display={display}
        depth={depth}
        className={className}
      />
    )
  }

  if (typeof value === 'object') {
    const record = value as Record<string, unknown>
    const properties = objectPropertiesForValue(record, propSchema)
    if (properties.length === 0) {
      return <JsonFallback value={value} className={className} />
    }
    return (
      <ObjectView record={record} properties={properties} depth={depth} className={className} />
    )
  }

  return <JsonFallback value={value} className={className} />
}

function ObjectView({
  record,
  properties,
  depth,
  className
}: {
  record: Record<string, unknown>
  properties: SchemaProperty[]
  depth: number
  className?: string
}) {
  const visible = properties.filter((prop) => !shouldOmitUnitKey(record, prop.key))

  if (isAllScalarObject(record)) {
    const only = visible.length === 1 ? visible[0] : undefined
    if (only?.display === 'kpi' && isScalar(record[only.key]) && !Array.isArray(record[only.key])) {
      return (
        <KpiCard
          label={only.title}
          value={record[only.key]}
          schema={only.schema}
          unit={attachedUnit(record, only.key)}
          className={className}
        />
      )
    }
    return (
      <dl className={cn('m-0 flex w-full flex-col gap-3', className)}>
        {visible.map((prop) => (
          <ScalarDlRow
            key={prop.key}
            prop={prop}
            value={record[prop.key]}
            record={record}
            depth={depth}
          />
        ))}
      </dl>
    )
  }

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {visible.map((prop) => (
        <PropertyBlock
          key={prop.key}
          prop={prop}
          value={record[prop.key]}
          record={record}
          depth={depth}
        />
      ))}
    </div>
  )
}

function PropertyBlock({
  prop,
  value,
  record,
  depth
}: {
  prop: SchemaProperty
  value: unknown
  record: Record<string, unknown>
  depth: number
}) {
  const nestedObject =
    value !== null && typeof value === 'object' && !Array.isArray(value) && !isScalar(value)

  if (prop.display === 'kpi' && isScalar(value) && !Array.isArray(value)) {
    return (
      <KpiCard
        label={prop.title}
        value={value}
        schema={prop.schema}
        unit={attachedUnit(record, prop.key)}
      />
    )
  }

  if (typeof value === 'string' && (prop.display === 'markdown' || looksLikeMarkdown(value))) {
    return (
      <div className="flex flex-col gap-1.5">
        <div className={groupHeaderClass(depth)}>{prop.title}</div>
        <div className="prose prose-sm dark:prose-invert max-w-none text-ink-2">
          <LazyChatMarkdown>{value}</LazyChatMarkdown>
        </div>
      </div>
    )
  }

  if (nestedObject || Array.isArray(value)) {
    return (
      <div className="flex flex-col gap-1.5">
        <div className={groupHeaderClass(depth)}>{prop.title}</div>
        <SchemaAwareResultView value={value} schema={prop.schema} depth={depth + 1} />
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col gap-1">
      <div className="text-xs text-ink-3">{prop.title}</div>
      <div className={cn('w-full', bodyTextClass(depth))}>
        <ScalarValue value={value} schema={prop.schema} unit={attachedUnit(record, prop.key)} />
      </div>
    </div>
  )
}

function ScalarDlRow({
  prop,
  value,
  record,
  depth
}: {
  prop: SchemaProperty
  value: unknown
  record: Record<string, unknown>
  depth: number
}) {
  return (
    <div className="flex w-full flex-col gap-1">
      <dt className="text-xs text-ink-3">{prop.title}</dt>
      <dd className={cn('m-0 w-full', bodyTextClass(depth))}>
        <ScalarValue value={value} schema={prop.schema} unit={attachedUnit(record, prop.key)} />
      </dd>
    </div>
  )
}

function ScalarValue({
  value,
  schema,
  unit
}: {
  value: unknown
  schema?: Record<string, unknown>
  unit?: string
}) {
  if (typeof value === 'boolean') {
    return <span>{value ? 'Yes' : 'No'}</span>
  }

  if (typeof value === 'number' && isUnitIntervalSchema(schema) && value >= 0 && value <= 1) {
    const tier = severityTier(value)
    return (
      <span
        className={cn(
          'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
          TIER_PILL_CLASSES[tier]
        )}
      >
        {formatScalar(value, schema)}
      </span>
    )
  }

  if (typeof value === 'string' && schemaEnumValues(schema)) {
    const tone = enumTone(value)
    const toneClass =
      tone === 'neutral' ? 'border border-line bg-bg-elev text-ink-2' : TIER_PILL_CLASSES[tone]
    return (
      <span
        className={cn(
          'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
          toneClass
        )}
      >
        {value}
      </span>
    )
  }

  const formatted = formatScalar(value, schema)
  if (unit && typeof value === 'number' && !formatted.endsWith('%')) {
    return <span>{`${formatted} ${unit}`}</span>
  }
  return <span>{formatted}</span>
}

function KpiCard({
  label,
  value,
  schema,
  unit,
  className
}: {
  label?: string
  value: unknown
  schema?: Record<string, unknown>
  unit?: string
  className?: string
}) {
  const formatted = formatScalar(value, schema)
  const display =
    unit && typeof value === 'number' && !formatted.endsWith('%')
      ? `${formatted} ${unit}`
      : formatted
  const tierClass =
    typeof value === 'number' && isUnitIntervalSchema(schema) && value >= 0 && value <= 1
      ? TIER_TEXT_CLASSES[severityTier(value)]
      : 'text-ink'
  return (
    <div className={cn('rounded-xl border border-line bg-card px-4 py-3 shadow-sm', className)}>
      {label ? (
        <div className="text-11 font-medium uppercase tracking-wide text-ink-4">{label}</div>
      ) : null}
      <div
        className={cn(
          'text-2xl font-semibold tracking-tight tabular-nums',
          label ? 'mt-1' : null,
          tierClass
        )}
      >
        {display}
      </div>
    </div>
  )
}

function ArrayView({
  value,
  schema,
  display,
  depth,
  className
}: {
  value: unknown[]
  schema: Record<string, unknown> | null
  display?: string
  depth: number
  className?: string
}) {
  if (value.length === 0) {
    return <span className={cn(bodyTextClass(depth), 'text-ink-3', className)}>—</span>
  }

  if (value.every(isScalar)) {
    return (
      <div className={cn('flex flex-wrap gap-1.5', className)}>
        {value.map((item, i) => (
          <span
            key={i}
            className="rounded-md border border-line bg-card px-2 py-0.5 text-xs text-ink-2"
          >
            {formatScalar(item)}
          </span>
        ))}
      </div>
    )
  }

  if (isObjectArray(value) && (display === 'table' || display === undefined)) {
    const cols = schema ? itemObjectProperties(schema) : []
    const keys =
      cols.length > 0
        ? cols.map((c) => c.key)
        : Array.from(new Set(value.flatMap((row) => Object.keys(row))))
    const titles = new Map(cols.map((c) => [c.key, c.title]))

    return (
      <div className={cn('overflow-hidden rounded-xl border border-line bg-card', className)}>
        <div className="overflow-x-auto">
          <table className="w-full caption-bottom text-sm">
            <thead>
              <tr className="border-b border-line">
                {keys.map((key) => (
                  <th
                    key={key}
                    className="h-9 px-3 text-left align-middle text-xs font-medium text-ink-3"
                  >
                    {titles.get(key) ?? humanizeKey(key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {value.map((row, i) => (
                <tr key={i} className="border-b border-line last:border-0">
                  {keys.map((key) => {
                    const col = cols.find((c) => c.key === key)
                    const cell = row[key]
                    return (
                      <td key={key} className={cn('px-3 py-2 align-top', bodyTextClass(depth))}>
                        {isScalar(cell) || (Array.isArray(cell) && cell.every(isScalar)) ? (
                          <ScalarValue
                            value={cell}
                            schema={col?.schema}
                            unit={attachedUnit(row, key)}
                          />
                        ) : (
                          <SchemaAwareResultView
                            value={cell}
                            schema={col?.schema}
                            depth={depth + 1}
                          />
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  return <JsonFallback value={value} className={className} />
}

function JsonFallback({ value, className }: { value: unknown; className?: string }) {
  return (
    <pre
      className={cn(
        'm-0 max-h-96 overflow-auto rounded-xl border border-line bg-card px-4 py-3 text-xs leading-relaxed text-ink-2',
        className
      )}
    >
      {JSON.stringify(value, null, 2)}
    </pre>
  )
}

function looksLikeMarkdown(value: string): boolean {
  // Cheap heuristic — only used when no explicit display hint is set.
  return /^#{1,6}\s|^\*\*|^\s*[-*]\s|\n\n/.test(value)
}

function groupHeaderClass(depth: number): string {
  return depth === 0 ? 'text-sm font-semibold text-ink' : 'text-sm font-medium text-ink'
}

function bodyTextClass(depth: number): string {
  return depth >= 1 ? 'text-xs text-ink-2' : 'text-sm text-ink-2'
}
